import { useState, type JSX } from 'react';
import { Hospital, ShieldAlert } from 'lucide-react';

import { LoginForm } from './login-form/login-form.js';
import { MfaChallenge } from './mfa-challenge/mfa-challenge.js';

export interface AuthPageProps {
  onAuthenticated: (token: string) => void;
}

type AuthStep =
  | { type: 'login' }
  | { type: 'mfa'; mfaTicket: string; expiresAt: string };

export function AuthPage({ onAuthenticated }: AuthPageProps): JSX.Element {
  const [step, setStep] = useState<AuthStep>({ type: 'login' });

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-slate-950 px-4 text-slate-100">
      <div className="w-full max-w-md rounded-xl border border-slate-800 bg-slate-900/90 p-8 shadow-2xl backdrop-blur-sm">
        <header className="mb-8 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
            <Hospital className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-bold tracking-tight text-slate-100">
            CTMS Cockpit
          </h1>
          <p className="mt-1 text-xs text-slate-400">
            System Zarządzania Ośrodkiem Badań Klinicznych
          </p>
          <div className="mt-2 inline-flex items-center gap-1 rounded-full border border-slate-800 bg-slate-950/60 px-2.5 py-0.5 text-[11px] text-slate-400">
            <ShieldAlert className="h-3 w-3 text-cyan-400" />
            <span>Środowisko bezpieczne · Wymagane 2FA</span>
          </div>
        </header>

        {step.type === 'login' ? (
          <LoginForm
            onSuccess={onAuthenticated}
            onMfaRequired={(mfaTicket, expiresAt) =>
              setStep({ type: 'mfa', mfaTicket, expiresAt })
            }
          />
        ) : (
          <MfaChallenge
            mfaTicket={step.mfaTicket}
            expiresAt={step.expiresAt}
            onSuccess={onAuthenticated}
            onCancel={() => setStep({ type: 'login' })}
          />
        )}

        <footer className="mt-8 border-t border-slate-800/80 pt-4 text-center text-[11px] text-slate-500">
          Dostęp wyłącznie dla upoważnionego personelu medycznego i administracyjnego.
        </footer>
      </div>
    </div>
  );
}
